<template>
  <v-container>
    <v-row>
      <v-col :cols="9">
        <v-file-input
          accept=".jpg,.jpeg,.png,.mp4,.mov,.pdf,.kmz,.cad,.shp,.shx,.dbf,.prj,.heic"
          label="Select a file (JPG, PNG, MP4, MOV, PDF, KMZ, CAD, SHP, SHX, DBF, PRJ, HEIC)..."
          v-model="files"
          multiple
          small-chips
          show-size
          counter
        ></v-file-input>
      </v-col>
      <v-col :cols="3">
        <v-btn dark block @click="uploadFiles">Upload</v-btn>
      </v-col>
    </v-row>

    <v-dialog v-model="loading" fullscreen>
      <v-container fluid fill-height class="dialog">
        <v-layout justify-center align-center>
          <v-card>
            <v-card-title>
              <span v-if="!uploadDone">Uploading...</span>
              <span v-else>Upload Complete</span>
            </v-card-title>
            <v-data-iterator
              :items="fileList"
              :items-per-page="-1"
              hide-default-footer
            >
              <template v-slot:default="props">
                <v-list class="pr-2 overflow-y-auto" max-height="75vh">
                  <v-list-item
                    v-for="item in props.items"
                    :key="item.file.name"
                    class="pr-0"
                  >
                    <v-list-item-content class="py-1">
                      <v-container class="pa-0">
                        <v-layout
                          align-center
                          justify-space-between
                          class="pa-0"
                        >
                          {{ item.file.name }}
                          <v-progress-circular
                            :rotate="360"
                            :size="60"
                            :value="item.uploadPercentage"
                            color="primary"
                            class="ml-8"
                            style="min-width: 60px"
                            >{{ item.uploadPercentage }}
                          </v-progress-circular>
                        </v-layout>
                      </v-container>
                    </v-list-item-content>
                  </v-list-item>
                </v-list>
              </template>
            </v-data-iterator>
            <v-card-actions>
              <v-spacer></v-spacer>
              <v-btn
                :disabled="!uploadDone"
                color="primary"
                @click="loading = false"
              >
                OK
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-layout>
      </v-container>
    </v-dialog>

    <v-dialog v-model="confirmOk" width="500">
      <v-card>
        <v-card-title class="headline grey lighten-2" primary-title>
          Success!
        </v-card-title>

        <v-card-text class="pa-4">
          Your attachments have uploaded successfully.
        </v-card-text>

        <v-divider></v-divider>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="primary" @click="confirmOk = false"> OK </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="error" width="500">
      <v-card>
        <v-card-title class="headline grey lighten-2" primary-title>
          Something went wrong...
        </v-card-title>

        <v-card-text class="pa-4">
          <v-icon>mdi-alert</v-icon>
          An error has occurred. Please refresh the page and try again.
        </v-card-text>

        <v-divider></v-divider>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="primary" @click="error = false"> OK </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="invalidFileError" width="500">
      <v-card>
        <v-card-title class="headline grey lighten-2" primary-title>
          Invalid file selection!
        </v-card-title>

        <v-card-text class="pa-4">
          No files have been uploaded because some files have extensions that
          aren't supported.
          <br /><br />
          The following files cannot be uploaded:
          <ul>
            <li v-for="file in invalidFiles" v-bind:key="file">
              {{ file }}
            </li>
          </ul>
        </v-card-text>

        <v-divider></v-divider>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="primary" @click="invalidFileError = false"> OK </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import axios from "axios";
import {
  appBucketName,
  attachmentsBucketName,
  signingUrlLocatorFileName
} from "../../aws/config";

import { newtinMixin } from "@/mixins/newtinMixin.js";
import { awsFileUploadMixin } from "@/mixins/awsFileUploadMixin.js";

const appBucketUrl = `https://${appBucketName}.s3-us-west-1.amazonaws.com`;
const attachmentsBucketUrl = `https://${attachmentsBucketName}.s3-us-west-1.amazonaws.com`;

const lookupUrl = `${appBucketUrl}/${signingUrlLocatorFileName}`;

export default {
  mixins: [newtinMixin, awsFileUploadMixin],
  name: "UploadAttachment",
  props: {
    guid: {
      type: String
    }
  },
  data: () => ({
    files: null,
    fileList: [],
    error: null,
    invalidFileError: false,
    invalidFiles: null,
    loading: false,
    uploadDone: false,
    confirmOk: false
  }),
  mounted: async function() {
    try {
      const response = await axios.get(lookupUrl);
      this.signingUrl = response.data;
    } catch (lookupError) {
      this.error = true;
    }
  },
  watch: {
    files() {
      if (this.files !== null && this.files.length) {
        this.fileList = this.files.map(file => ({
          file,
          uploadPercentage: 0
        }));
      }
    }
  },
  methods: {
    uploadFiles: async function() {
      if (!this.files) {
        return;
      }

      const invalidFiles = this.files.filter(
        file =>
          !file.name.match(
            /\.(jpg|jpeg|png|mp4|mov|pdf|kmz|cad|shp|shx|dbf|prj|heic)$/i
          )
      );

      if (invalidFiles.length > 0) {
        this.invalidFiles = invalidFiles.map(invalidFile => invalidFile.name);
        this.invalidFileError = true;
        return;
      }

      const uploadPromises = [];

      for (let file of this.files) {
        uploadPromises.push(this.uploadFile(file));
      }

      this.uploadDone = false;

      try {
        await Promise.all(uploadPromises);
        this.files = null;
      } catch (error) {
        this.error = true;
      } finally {
        this.uploadDone = true;
      }
    },
    uploadFile: async function(file) {
      const uploadDate = new Date();
      const bucketFolder = this.getBucketFolder(uploadDate);
      const fileConfig = this.getFileNameConfig(
        undefined,
        undefined,
        file.name,
        uploadDate
      );
      const fileName = fileConfig.fullFileName;
      const { fileExtension } = fileConfig;

      const signatureRequestUrl = `${this.signingUrl}?bucketFolder=${bucketFolder}&fileName=${fileName}`;

      this.loading = true;

      let signedUrl;

      const signedUrlResponse = await axios.get(signatureRequestUrl);
      signedUrl = signedUrlResponse.data.signedUrl;

      const options = {
        headers: {
          "Content-Type": "application/octet-stream",
          "x-amz-tagging": `center=UNCC&prod=true`
        }
      };

      await axios.put(signedUrl, file, options);

      const uploadedFileUrl = `${attachmentsBucketUrl}/${bucketFolder}/${fileName}`;
      const encodedUrl = encodeURI(uploadedFileUrl);
      const encodedFileName = encodeURI(fileName);

      const newtinUrl = `${this.getNewtinUrl(
        window.location.href
      )}/postAttachment.vjs`;

      let currentFileName = file.name;
      const newtinUploadUrl = `${newtinUrl}?ATTACHMENTS_CODE=${this.guid}&FILE_TYPE=${fileExtension}&FILE_NAME=${encodedFileName}&FILE_URL=${encodedUrl}&FILE_SIZE=${file.size}`;
      /* istanbul ignore next */
      const newtinUploadOptions = {
        headers: {
          "Content-Type": "multipart/form-data"
        },
        onUploadProgress: function(progressEvent) {
          this.fileList.find(data => {
            if (data.file.name === currentFileName) {
              data.uploadPercentage = parseInt(
                Math.round((progressEvent.loaded / progressEvent.total) * 100)
              );
            }
          });
        }.bind(this)
      };

      let formData = new FormData();
      formData.append("file", file);

      const res = await axios.post(
        newtinUploadUrl,
        formData,
        newtinUploadOptions
      );

      if (res.data.err || res.data.result != "OK") {
        throw res.data;
      }
    }
  }
};
</script>

<style scoped>
.dialog {
  background-color: rgba(0, 0, 0, 0.85);
}
.v-btn {
  background-image: linear-gradient(to right, #2685f4, #3a3dd1) !important;
}
</style>
